import { z } from 'zod';
import type { AnswerDifficulty, AnswerKeyEntry } from './answer-key.entity';
import { toAnswerOption } from '@/modules/simulado/domain/entities/answer.entity';

/**
 * O diagnóstico do ensaio: **onde a pessoa perdeu ponto**, e não o que vai
 * acontecer com a nota dela na prova real.
 *
 * **Portado do `enamed-feedback-lp` (a "leitura de padrão") sem mudar a
 * conta**, e com o teste de lá junto. Lá o texto deste cabeçalho explicava por
 * que o padrão não move a nota do ENAMED (Rasch, Nota Técnica 42/2025 do INEP);
 * aqui as provas são outras, então o que vale é só a parte que não depende da
 * régua de nenhuma banca: errar fácil acima do esperado aponta ponto perdido
 * por leitura, pressa ou marcação, que é o mais barato de recuperar. É
 * diagnóstico de estudo, não previsão de nota.
 *
 * Os limiares de `DEFAULT_TENDENCIA_CONFIG` foram calibrados para as 100
 * questões do ENAMED. Num simulado mais curto eles ficam mais frouxos, e
 * recalibrá-los por tamanho de simulado é decisão em aberto.
 */
export const TENDENCIA_CATEGORIES = [
  /** Excesso pequeno: os erros ficaram onde falta conteúdo. */
  'ERROS_NAS_DIFICEIS',
  /** Excesso dentro do previsto para aquele total de acertos. */
  'DISTRIBUICAO_ESPERADA',
  /** Excesso alto: ponto perdido em questão que a pessoa provavelmente sabia. */
  'PERDA_NAS_FACEIS',
  'SEM_LEITURA',
] as const;

export type TendenciaCategory = (typeof TENDENCIA_CATEGORIES)[number];

/**
 * Por que não houve leitura. Os dois são abstenção deliberada, não falha:
 *
 * - `AMOSTRA`: questões demais em branco. Sem saber o que a pessoa marcou, ler
 *   o padrão dela seria inventar.
 * - `PISO`: para aquela faixa de acertos, o esperado de fáceis erradas já é tão
 *   alto que a métrica perde poder de separação. Quase todo mundo cairia no
 *   excesso pequeno, e o resultado não diria nada.
 */
export type SemLeituraReason = 'AMOSTRA' | 'PISO';

/**
 * As quatro constantes da especificação, e **as únicas do sistema**. No ENAMED
 * elas vêm do Remote Config para recalibrar sem deploy; aqui, por enquanto, só
 * os valores de lançamento.
 */
export const TendenciaConfigSchema = z.object({
  /**
   * Até aqui o excesso é pequeno: `ERROS_NAS_DIFICEIS`. O nome guarda o
   * vocabulário antigo ("favorável") porque é a chave publicada no ENAMED.
   */
  xFavoravelMax: z.number().int().min(0),
  /** Até aqui é `DISTRIBUICAO_ESPERADA`; acima, `PERDA_NAS_FACEIS`. */
  xNeutroMax: z.number().int().min(0),
  /** Acima deste tanto de questões em branco, não há leitura. */
  maxNaoLembro: z.number().int().min(0),
  /** Fração das fáceis a partir da qual a métrica perde sentido. */
  fracaoPiso: z.number().min(0).max(1),
});

export type TendenciaConfig = z.infer<typeof TendenciaConfigSchema>;

/** Valores de lançamento do ENAMED. */
export const DEFAULT_TENDENCIA_CONFIG: TendenciaConfig = {
  xFavoravelMax: 2,
  xNeutroMax: 6,
  maxNaoLembro: 10,
  fracaoPiso: 0.4,
};

/**
 * O resultado da leitura. Os nomes são descritivos, e o comentário guarda a
 * letra da especificação — elas aparecem nos textos da tela como `{T}`, `{OF}`,
 * `{EEF}`, `{X}` e `{NL}`, e o log da seção 5 é gravado por elas.
 */
export interface TendenciaReading {
  category: TendenciaCategory;
  /** Preenchido só em `SEM_LEITURA`. */
  reason: SemLeituraReason | null;
  /** `T` — acertos no universo válido. */
  acertos: number;
  /** `N` — tamanho do universo válido. */
  questoesValidas: number;
  /** `OF` — fáceis erradas observadas. */
  faceisErradas: number;
  /** `EEF` — fáceis erradas que o total de acertos já previa. */
  faceisErradasEsperadas: number;
  /** `X` — o excesso, que é o que classifica. Nunca negativo. */
  excesso: number;
  /** `AD` — difíceis certas. Só para log e exibição, não entra na conta. */
  dificeisCertas: number;
  /** `NL` — questões válidas que ficaram em branco. */
  naoLembro: number;
  /**
   * Questões que ficaram **fora** da leitura por não terem dificuldade
   * classificada. Não é erro nem abstenção: é prova que ainda não foi analisada.
   * A tela usa para dizer sobre quantas questões a leitura fala.
   */
  naoClassificadas: number;
  /**
   * O aproveitamento faixa a faixa, e a decomposição exata do que aparece no
   * topo do cartão: os três `total` somam `questoesValidas`, e os três
   * `acertos` somam `acertos`. É por isso que a faixa conta só o que a pessoa
   * **respondeu** — misturar branco aqui faria as barras não fecharem com o
   * número grande logo acima, e ninguém confere duas contas diferentes na mesma
   * tela sem achar que uma está errada.
   */
  porDificuldade: Record<AnswerDifficulty, DifficultyTally>;
}

/**
 * A faixa de dificuldade, decomposta.
 *
 * `respondidas` chamava-se `total`, e o nome mudou porque com o branco em cena
 * "total" ficou ambíguo: o total da faixa é `respondidas + emBranco`.
 *
 * **É de `respondidas` que saem `nF`, `nM` e `nD`**, e não do total. O universo
 * válido da especificação exclui a abstenção, e trocar a base aqui mudaria o
 * esperado de fáceis erradas e, com ele, a categoria de todo mundo.
 */
export interface DifficultyTally {
  respondidas: number;
  acertos: number;
  emBranco: number;
}

/**
 * As questões que entram na leitura: valem, têm resposta publicada e têm
 * dificuldade.
 *
 * **Classificação parcial é estado normal**, não defeito — a análise das
 * questões é incremental, e sai aos poucos. Cada questão sem dificuldade fica
 * fora do universo, como uma anulada, e a leitura fala do que já foi
 * classificado. Não há alternativa honesta: sem dificuldade a questão não tem
 * lugar na régua, e chutar uma faixa inventaria o dado que decide a categoria.
 *
 * A consequência a ter em mente é que **a leitura sobre um pedaço da prova pode
 * diferir da leitura sobre ela inteira**. Por isso a tela diz quantas questões
 * ela cobre quando a classificação ainda não terminou.
 */
export function readableQuestions(entries: AnswerKeyEntry[]): AnswerKeyEntry[] {
  return entries.filter(
    (entry) =>
      !entry.isExcluded && entry.answer !== null && entry.difficulty != null,
  );
}

/**
 * Quais questões válidas ainda estão sem dificuldade.
 *
 * Existe para quem publica: o gabarito é cem entradas mantidas à mão no console
 * do Remote Config, e saber **onde** faltou é o que evita conferir a lista
 * inteira de novo.
 */
export function unclassifiedQuestions(entries: AnswerKeyEntry[]): number[] {
  return entries
    .filter(
      (entry) =>
        !entry.isExcluded && entry.answer !== null && entry.difficulty == null,
    )
    .map((entry) => entry.number);
}

/**
 * A abstenção, como índice.
 *
 * **É convenção nossa, e não vai para o servidor**: a rota de envio recusa valor
 * negativo com `400`, e o `answerKey` que ela guarda é esparso — questão em
 * branco simplesmente não tem chave. O `-1` nasce na leitura, em
 * `toSubmittedAnswers`, para que a abstenção seja um valor explícito em vez de
 * uma ausência que cada consumidor precisa lembrar de interpretar.
 *
 * É o `NAO_LEMBRO` do contrato de entrada da especificação. Se um dia a folha
 * ganhar um botão de "não lembro" de verdade e o back passar a aceitá-lo, só
 * muda de onde o `-1` vem — nada daqui para baixo.
 */
export const NAO_LEMBRO = -1;

/**
 * As respostas da pessoa no formato que a especificação pede: **uma entrada por
 * questão do gabarito**, com o índice que ela marcou ou `NAO_LEMBRO`.
 *
 * O envio é esparso, então quem sabe quantas questões existiam é o gabarito
 * oficial — nunca `TOTAL_QUESTIONS`, que é palpite do front sobre uma decisão da
 * edição. Percorrer o gabarito também descarta sozinho a resposta enviada para
 * questão que ele não publica.
 */
export function toSubmittedAnswers(
  answerKey: AnswerKeyEntry[],
  submitted: Record<string, number>,
): Record<number, number> {
  const answers: Record<number, number> = {};

  for (const entry of answerKey) {
    // `??` e não `||`: o índice `0` é a alternativa A, e não uma ausência.
    answers[entry.number] = submitted[String(entry.number)] ?? NAO_LEMBRO;
  }

  return answers;
}

/**
 * A leitura de tendência, passo a passo como na especificação.
 *
 * **Percorre o gabarito oficial, e não as respostas da pessoa.** É de propósito,
 * e resolve três coisas de uma vez: só o gabarito sabe quantas questões
 * existiam; questão excluída que ficou em branco não pode contar como
 * abstenção; e resposta de questão que o gabarito não publica é descartada.
 *
 * As respostas chegam como índice, no formato de `toSubmittedAnswers`. Três
 * coisas viram abstenção pelo mesmo caminho, e é de propósito: o `NAO_LEMBRO`
 * explícito, a chave ausente (para quem passar um mapa esparso), e o índice
 * fora das alternativas conhecidas. Nos três casos não há alternativa marcada,
 * e inventar uma seria pior do que contar como em branco.
 */
export function readTendencia(
  answerKey: AnswerKeyEntry[],
  answers: Record<number, number>,
  config: TendenciaConfig = DEFAULT_TENDENCIA_CONFIG,
): TendenciaReading {
  // Passos 1 e 3 na mesma passada: são a mesma varredura, e separá-las só
  // percorreria a prova duas vezes para chegar ao mesmo lugar.
  let questoesValidas = 0; // N
  let acertos = 0; // T
  let naoLembro = 0; // NL
  let naoClassificadas = 0;

  // As contagens por faixa são a fonte de tudo o que depende de dificuldade:
  // `nF`/`nM`/`nD` do passo 4, as fáceis erradas e as difíceis certas saem
  // daqui. Um acumulador só, em vez de seis contadores soltos que precisam ser
  // mantidos em acordo à mão.
  const porDificuldade: Record<AnswerDifficulty, DifficultyTally> = {
    EASY: { respondidas: 0, acertos: 0, emBranco: 0 },
    MEDIUM: { respondidas: 0, acertos: 0, emBranco: 0 },
    HARD: { respondidas: 0, acertos: 0, emBranco: 0 },
  };

  for (const entry of answerKey) {
    // Anulada ou em análise: fora do universo, e não conta nem como abstenção.
    if (entry.isExcluded) continue;
    // Índice publicado fora das alternativas que este app conhece. Sem resposta
    // certa não há o que comparar, então a questão sai como uma anulada.
    if (entry.answer === null) continue;
    // Ainda sem classificação. Fica de fora, como uma anulada: sem dificuldade
    // a questão não tem lugar na régua, e mantê-la quebraria a identidade
    // nF + nM + nD === N de que o passo 4 depende. Ver `readableQuestions`.
    if (!entry.difficulty) {
      naoClassificadas += 1;
      continue;
    }

    // `toAnswerOption` devolve `null` para o `NAO_LEMBRO` e para qualquer índice
    // fora das alternativas — os dois são "nenhuma alternativa marcada".
    const index = answers[entry.number];
    const marked = index === undefined ? null : toAnswerOption(index);
    const tally = porDificuldade[entry.difficulty];

    if (marked === null) {
      naoLembro += 1;
      // A abstenção fica **fora** do universo, mas dentro da faixa: é assim que
      // a tela consegue dizer onde a pessoa deixou de marcar sem que o branco
      // entre na conta que decide a categoria.
      tally.emBranco += 1;
      continue;
    }

    questoesValidas += 1;
    const isCorrect = marked === entry.answer;
    if (isCorrect) acertos += 1;

    tally.respondidas += 1;
    if (isCorrect) tally.acertos += 1;
  }

  const nF = porDificuldade.EASY.respondidas;
  const nM = porDificuldade.MEDIUM.respondidas;
  const nD = porDificuldade.HARD.respondidas;
  const faceisErradas = nF - porDificuldade.EASY.acertos; // OF
  const dificeisCertas = porDificuldade.HARD.acertos; // AD

  const erros = questoesValidas - acertos; // E

  /**
   * Passo 4 — o número que faz a régua deslizar com o total.
   *
   * O preenchimento ideal erra primeiro as difíceis, depois as médias, e só
   * então começa a errar fáceis. Então os erros que "sobram" depois de consumir
   * difíceis e médias são os que necessariamente caíram em fáceis: quem acertou
   * pouco **tem** de errar fáceis, e isso não é incoerência.
   */
  const faceisErradasEsperadas = Math.max(0, erros - (nD + nM)); // EEF

  const reading = {
    acertos,
    questoesValidas,
    faceisErradas,
    faceisErradasEsperadas,
    // Passo 6 — o excesso. Travado em zero: acertar mais fáceis do que o total
    // previa é coerência de sobra, não um número negativo para exibir.
    excesso: Math.max(0, faceisErradas - faceisErradasEsperadas), // X
    dificeisCertas,
    naoLembro,
    naoClassificadas,
    porDificuldade,
  };

  // Passo 2 — abstenção por amostra, e vem antes do piso de propósito: quem
  // deixou meia prova em branco não tem padrão a ler, qualquer que seja o piso.
  if (naoLembro > config.maxNaoLembro) {
    return { ...reading, category: 'SEM_LEITURA', reason: 'AMOSTRA' };
  }

  /**
   * Passo 5 — abstenção por piso.
   *
   * Quando o esperado já cobre boa parte das fáceis, a métrica não separa mais
   * ninguém: o excesso tende a zero para todo mundo e a leitura sairia sempre
   * no mesmo lugar, por construção.
   *
   * Prova sem nenhuma fácil cai sempre aqui, porque o piso vira zero e a
   * comparação passa a ser sempre verdadeira. É o resultado certo — sem fáceis
   * não há o que ler —, mas é silencioso, então fica dito.
   */
  if (faceisErradasEsperadas >= config.fracaoPiso * nF) {
    return { ...reading, category: 'SEM_LEITURA', reason: 'PISO' };
  }

  if (reading.excesso <= config.xFavoravelMax) {
    return { ...reading, category: 'ERROS_NAS_DIFICEIS', reason: null };
  }
  if (reading.excesso <= config.xNeutroMax) {
    return { ...reading, category: 'DISTRIBUICAO_ESPERADA', reason: null };
  }
  return { ...reading, category: 'PERDA_NAS_FACEIS', reason: null };
}
